import { Link } from "react-router-dom";
import { Download, Printer, AlertCircle, FileText, Image as ImageIcon, ArrowRight, ClipboardCheck, ShieldAlert, Stethoscope, Megaphone, Handshake, Sparkles } from "lucide-react";
import SectionHeading from "../components/SectionHeading";

const FORMS = [
  {
    id: "physical",
    name: "2026 Pop Warner Physical Form",
    file: "/assets/forms/physical-form-2026.pdf",
    type: "PDF",
    icon: Stethoscope,
    mustPrint: true,
    description: "Official medical clearance form for the 2026 season. Required for every athlete before first practice.",
    cta: "Print & Fill Out",
  },
  {
    id: "parental",
    name: "2026 Parental Consent Form",
    file: "/assets/forms/parental-consent-2026.pdf",
    type: "PDF",
    icon: ClipboardCheck,
    mustPrint: true,
    description: "Parent or legal guardian authorization for participation. Signature required.",
    cta: "Print & Fill Out",
  },
  {
    id: "conduct",
    name: "Codes of Conduct",
    file: "/assets/forms/code-of-conduct.pdf",
    type: "PDF",
    icon: ShieldAlert,
    mustPrint: false,
    description: "Standards of behavior expected of athletes, parents, and coaches throughout the Titans program.",
    cta: "Download PDF",
  },
  {
    id: "cheer-flyer",
    name: "Titans Cheer Flyer · 2026 Season",
    file: "/assets/cheer-flyer.png",
    type: "Image",
    icon: Sparkles,
    mustPrint: false,
    description: "Share with friends and family. Ages 6–13 · $225 all-in (uniform, bow &amp; practice wear). Contact Autumn Andrews (951) 590-6349.",
    cta: "View / Download",
  },
  {
    id: "flyer",
    name: "Team Flyer · 2026 Season",
    file: "/assets/forms/team-flyer.jpg",
    type: "Image",
    icon: Megaphone,
    mustPrint: false,
    description: "Share with friends and family. Mt. Baldy Pop Warner Conference flyer for Cawston Titans Football & Cheer.",
    cta: "View / Download",
  },
  {
    id: "sponsor",
    name: "Sponsor Letter · 2026 Season",
    file: "/assets/forms/sponsor-letter.pdf",
    type: "PDF",
    icon: Handshake,
    mustPrint: false,
    description: "Information for prospective sponsors. Help fund equipment, uniforms, and field time for Titans athletes.",
    cta: "Download Sponsor Letter",
  },
];

export default function Forms() {
  return (
    <div data-testid="forms-page" className="bg-[#F5EFE0] py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <SectionHeading
          eyebrow="Downloads · 2026 Season"
          title="Required Forms &amp; Resources"
          description="Everything you need before first practice. Print, sign, and bring on July 20."
        />

        {/* PRINT REQUIRED NOTICE */}
        <div
          data-testid="print-required-notice"
          className="mb-12 flex flex-col md:flex-row items-start gap-4 bg-[#2D5F3F] text-[#FDFBF7] p-6 md:p-8 border-l-8 border-[#FDFBF7]"
        >
          <div className="shrink-0 w-12 h-12 bg-[#FDFBF7] text-[#2D5F3F] flex items-center justify-center">
            <Printer className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-xs uppercase tracking-[0.3em] font-bold opacity-85">Important</div>
            <h3 className="mt-1 font-display text-2xl md:text-3xl font-black uppercase leading-tight">
              The Parental Consent Form &amp; Physical Form Must Be Printed and Filled Out
            </h3>
            <p className="mt-3 text-[#FDFBF7]/90 leading-relaxed max-w-3xl">
              Both forms must be physically printed, completed by a parent or legal guardian (and licensed physician for the physical),
              and turned in to a coach at or before the first practice. Digital copies will not be accepted.
            </p>
          </div>
        </div>

        {/* FORMS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="forms-grid">
          {FORMS.map((f) => (
            <article
              key={f.id}
              data-testid={`form-card-${f.id}`}
              className={`relative group bg-white border p-7 md:p-8 transition-all hover:-translate-y-1 ${
                f.mustPrint ? "border-[#2D5F3F] shadow-pine-offset" : "border-black/15 hover:border-[#2D5F3F]/60"
              }`}
            >
              {f.mustPrint && (
                <div className="absolute -top-3 left-6 bg-[#2D5F3F] text-[#FDFBF7] px-3 py-1 font-display text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-1.5">
                  <AlertCircle className="w-3 h-3" /> Must Print &amp; Fill Out
                </div>
              )}

              <div className="flex items-start justify-between gap-4">
                <div className="w-14 h-14 bg-[#EFE9D9] flex items-center justify-center text-[#2D5F3F]">
                  <f.icon className="w-7 h-7" strokeWidth={2.2} />
                </div>
                <span className="font-display text-[10px] uppercase tracking-[0.3em] font-bold text-black/55 mt-2">
                  {f.type === "PDF" ? <FileText className="w-4 h-4 inline mr-1" /> : <ImageIcon className="w-4 h-4 inline mr-1" />}
                  {f.type}
                </span>
              </div>

              <h3 className="mt-6 font-display text-2xl md:text-3xl font-black uppercase text-[#0A0A0A] leading-tight">{f.name}</h3>
              <p className="mt-3 text-black/70 leading-relaxed">{f.description}</p>

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={f.file}
                  target="_blank"
                  rel="noreferrer"
                  download
                  data-testid={`form-download-${f.id}`}
                  className="inline-flex items-center gap-2 bg-[#2D5F3F] hover:bg-[#1B4332] text-[#FDFBF7] font-display uppercase tracking-wider text-sm font-extrabold px-5 py-3 transition-colors"
                >
                  <Download className="w-4 h-4" /> {f.cta}
                </a>
                <a
                  href={f.file}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`form-view-${f.id}`}
                  className="inline-flex items-center gap-2 border-2 border-[#2D5F3F] text-[#2D5F3F] hover:bg-[#2D5F3F] hover:text-[#FDFBF7] font-display uppercase tracking-wider text-sm font-bold px-5 py-3 transition-colors"
                >
                  Open
                </a>
              </div>
            </article>
          ))}
        </div>

        {/* DROP-OFF */}
        <div className="mt-14 bg-white border border-black/15 p-8 md:p-10" data-testid="dropoff-block">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="font-display text-xs uppercase tracking-[0.3em] text-[#2D5F3F] font-bold">Where To Drop Off</div>
              <h3 className="mt-2 font-display text-3xl md:text-4xl font-black uppercase text-[#0A0A0A] leading-tight">
                Bring completed forms to first practice
              </h3>
              <p className="mt-4 text-black/75 leading-relaxed">
                Rancho Viejo Middle School · 985 Cawston Ave N, Hemet, CA 92545. Practice begins <span className="text-[#2D5F3F] font-bold">July 20, 2026</span>.
                Don&apos;t forget your athlete&apos;s birth certificate and a copy of medical insurance.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col gap-3">
              <Link
                to="/register"
                data-testid="forms-cta-register"
                className="inline-flex justify-center items-center gap-2 bg-[#2D5F3F] hover:bg-[#1B4332] text-[#FDFBF7] font-display uppercase tracking-wider font-extrabold px-6 py-4 transition-colors"
              >
                Start Online Registration <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="mailto:Cawstontitans@gmail.com"
                data-testid="forms-cta-email"
                className="inline-flex justify-center items-center gap-2 border-2 border-[#2D5F3F] text-[#2D5F3F] hover:bg-[#2D5F3F] hover:text-[#FDFBF7] font-display uppercase tracking-wider font-bold px-6 py-4 transition-colors"
              >
                Cawstontitans@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
